"use client";

import { useMemo, useState, type ReactNode } from "react";
import { Api_Paths } from "@/lib/api/paths";
import type { ReportJob } from "@/lib/parcelq/types";
import { ParcelQ_Client } from "@/lib/parcelq/client";
import { Amenity_Name_Is_Unnamed, Amenity_Row_Matches_View } from "@/lib/parcelq/mappers";
import type {
  Amenity_Row,
  Amenity_View_Mode,
  Red_Flag_Row,
  Source_Row,
  Table_Row,
  Tax_History_Row,
  Verification_Status,
} from "./types";
import { Amenities_Map } from "./amenities-map";
import {
  ParcelQ_Btn_Secondary_Class,
  ParcelQ_Card_Class,
  ParcelQ_Heading_Class,
  ParcelQ_Label_Mono_Class,
} from "./parcelq-styles";

type Data_Table_Props = {
  Title: string;
  Rows: Table_Row[];
  Accent?: boolean;
};

type Source_Group = {
  Source: string;
  Source_Url?: string;
  Rows: Table_Row[];
};

type Snapshot_Fact = {
  Label: string;
  Value: string;
};

type Nav_Item = {
  Id: string;
  Label: string;
};

function Rows_Find_Value(Rows: Table_Row[], Labels: string[]): string | null {
  const Wanted = Labels.map((Label) => Label.toLowerCase());
  for (const Row of Rows) {
    if (Wanted.includes(Row.Label.toLowerCase()) && Row.Value && Row.Value !== "—") {
      return Row.Value;
    }
  }
  return null;
}

function Snapshot_Facts_Build(
  Parcel_Rows: Table_Row[],
  Assessment_Rows: Table_Row[],
  Zoning_Rows: Table_Row[],
): Snapshot_Fact[] {
  const Candidates: Array<[string, string | null]> = [
    ["Address", Rows_Find_Value(Parcel_Rows, ["Site address"])],
    ["Owner", Rows_Find_Value(Parcel_Rows, ["Recorded owner"])],
    ["Lot", Rows_Find_Value(Parcel_Rows, ["Lot size"])],
    ["Zoning", Rows_Find_Value(Zoning_Rows, ["Zoning designation"])],
    [
      "Assessed",
      Rows_Find_Value(Assessment_Rows, ["Gross assessed value", "Net taxable value", "Land value"]),
    ],
    ["Built", Rows_Find_Value(Parcel_Rows, ["Year built"])],
  ];

  return Candidates.filter((Entry): Entry is [string, string] => Boolean(Entry[1])).map(
    ([Label, Value]) => ({ Label, Value }),
  );
}

function Report_Section({
  Id,
  Index,
  Title,
  Meta,
  Actions,
  children,
}: {
  Id: string;
  Index?: string;
  Title: string;
  Meta: string;
  Actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section id={Id} className="scroll-mt-28">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4 border-b border-pq-border pb-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-baseline gap-3">
            {Index ? (
              <span className="font-mono text-xs tracking-[0.2em] text-amber-700">{Index}</span>
            ) : null}
            <h3 className="font-display text-xl font-semibold tracking-tight text-pq-fg sm:text-2xl">
              {Title}
            </h3>
          </div>
          <p className={`mt-2 ${ParcelQ_Label_Mono_Class}`}>{Meta}</p>
        </div>
        {Actions ? <div className="flex flex-wrap gap-2">{Actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

function Source_Cell({ Source, Source_Url }: { Source: string; Source_Url?: string }) {
  if (Source_Url) {
    return (
      <a
        href={Source_Url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 font-semibold text-amber-800 underline decoration-amber-600/70 underline-offset-2 transition hover:text-amber-950 hover:decoration-amber-800"
      >
        <span aria-hidden>↗</span>
        {Source}
      </a>
    );
  }
  return <span className="font-mono text-xs text-pq-fg-subtle">{Source}</span>;
}

function Rows_Group_By_Source(Rows: Table_Row[]): Source_Group[] {
  const Groups: Source_Group[] = [];
  for (const Row of Rows) {
    const Last = Groups[Groups.length - 1];
    if (Last && Last.Source === Row.Source && Last.Source_Url === Row.Source_Url) {
      Last.Rows.push(Row);
    } else {
      Groups.push({ Source: Row.Source, Source_Url: Row.Source_Url, Rows: [Row] });
    }
  }
  return Groups;
}

function Data_Table({ Title, Rows, Accent = false }: Data_Table_Props) {
  if (!Rows.length) return null;

  const Groups = Rows_Group_By_Source(Rows);
  const Single_Source = Groups.length === 1;
  const Shared_Source = Single_Source ? Groups[0] : null;

  return (
    <div
      className={`flex h-full flex-col overflow-hidden rounded-2xl ${ParcelQ_Card_Class} ${
        Accent ? "ring-1 ring-amber-400/30" : ""
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-900/10 bg-gradient-to-r from-amber-50 to-white px-5 py-4">
        <div>
          <p className="text-sm font-semibold text-pq-fg">{Title}</p>
          <p className={`mt-1 ${ParcelQ_Label_Mono_Class}`}>
            {Rows.length} field{Rows.length === 1 ? "" : "s"}
          </p>
        </div>
        {Shared_Source ? (
          <Source_Cell Source={Shared_Source.Source} Source_Url={Shared_Source.Source_Url} />
        ) : null}
      </div>
      <dl className="divide-y divide-slate-100">
        {Groups.map((Group) =>
          Group.Rows.map((Row, Index) => (
            <div
              key={`${Group.Source}-${Row.Label}-${Row.Value}-${Index}`}
              className="grid gap-1 px-5 py-3.5 sm:grid-cols-[minmax(7rem,34%)_1fr_auto] sm:gap-4"
            >
              <dt className="text-xs font-medium uppercase tracking-wide text-pq-fg-subtle">
                {Row.Label}
                {Row.Hint ? (
                  <span className="mt-1 block normal-case tracking-normal text-[11px] font-normal leading-snug text-pq-fg-subtle">
                    {Row.Hint}
                  </span>
                ) : null}
              </dt>
              <dd className="text-sm font-medium leading-relaxed text-pq-fg">
                {Row.Source_Url && (Row.Link_Primary || /open |portal|search/i.test(Row.Value)) ? (
                  <a
                    href={Row.Source_Url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-amber-800 underline decoration-amber-600/70 underline-offset-2 transition hover:text-amber-950"
                  >
                    {Row.Value}
                    <span className="ml-1 text-xs font-normal">(open link)</span>
                  </a>
                ) : (
                  Row.Value
                )}
              </dd>
              {!Single_Source && Index === 0 ? (
                <dd className="sm:text-right">
                  <Source_Cell Source={Group.Source} Source_Url={Group.Source_Url} />
                </dd>
              ) : (
                <dd className="hidden sm:block" />
              )}
            </div>
          )),
        )}
      </dl>
    </div>
  );
}

function Usd_Parse(Text: string): number | null {
  const Clean = Text.replace(/[$,\s]/g, "");
  if (!Clean || Clean === "—") return null;
  const Number_Value = Number(Clean);
  return Number.isFinite(Number_Value) ? Number_Value : null;
}

function Usd_Format(Amount: number): string {
  return `$${Math.round(Amount).toLocaleString()}`;
}

function Tax_Year_Groups_Build(Rows: Tax_History_Row[]): Array<{
  Tax_Year: string;
  Rows: Tax_History_Row[];
  Year_Total: string | null;
}> {
  const Groups: Array<{ Tax_Year: string; Rows: Tax_History_Row[]; Year_Total: string | null }> = [];
  for (const Row of Rows) {
    const Last = Groups[Groups.length - 1];
    if (Last && Last.Tax_Year === Row.Tax_Year) {
      Last.Rows.push(Row);
    } else {
      Groups.push({ Tax_Year: Row.Tax_Year, Rows: [Row], Year_Total: null });
    }
  }
  for (const Group of Groups) {
    let Sum = 0;
    let Count = 0;
    for (const Row of Group.Rows) {
      const Amount = Usd_Parse(Row.Total);
      if (Amount == null) continue;
      Sum += Amount;
      Count += 1;
    }
    Group.Year_Total = Count > 0 ? Usd_Format(Sum) : null;
  }
  return Groups;
}

function Tax_Status_Class(Status: string): string {
  const Lower = Status.toLowerCase();
  if (/paid|cleared|satisfied/.test(Lower)) {
    return "border-emerald-400/30 bg-emerald-500/10 text-emerald-200";
  }
  if (/due|unpaid|delinq|open|owing/.test(Lower)) {
    return "border-amber-400/35 bg-amber-500/10 text-amber-200";
  }
  return "border-pq-border bg-slate-100 text-pq-fg-subtle";
}

function Tax_History_Table({ Rows, Source_Url }: { Rows: Tax_History_Row[]; Source_Url?: string }) {
  if (!Rows.length) return null;

  const Groups = Tax_Year_Groups_Build(Rows);

  return (
    <div className={`overflow-hidden rounded-2xl ${ParcelQ_Card_Class}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-pq-border bg-gradient-to-r from-amber-50 to-white px-5 py-4">
        <div>
          <p className="text-sm font-semibold text-pq-fg">Tax bill history</p>
          <p className={`mt-1 ${ParcelQ_Label_Mono_Class}`}>
            {Groups.length} year{Groups.length === 1 ? "" : "s"} · {Rows.length} installment
            {Rows.length === 1 ? "" : "s"}
          </p>
        </div>
        {Source_Url ? (
          <a
            href={Source_Url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-xs text-amber-800 underline-offset-2 hover:underline"
          >
            Tax Assessor portal
          </a>
        ) : null}
      </div>

      <ol className="divide-y divide-slate-200">
        {Groups.map((Group, Group_Index) => (
          <li key={`${Group.Tax_Year}-${Group_Index}`} className="px-5 py-5 sm:px-6">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="flex items-baseline gap-3">
                <span className="font-display text-2xl font-semibold tracking-tight text-pq-fg">
                  {Group.Tax_Year}
                </span>
                <span className={ParcelQ_Label_Mono_Class}>
                  {Group.Rows.length} installment{Group.Rows.length === 1 ? "" : "s"}
                </span>
              </div>
              {Group.Year_Total ? (
                <div className="text-right">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-pq-fg-subtle">
                    Year total
                  </p>
                  <p className="mt-1 font-mono text-sm font-medium text-amber-950">{Group.Year_Total}</p>
                </div>
              ) : null}
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {Group.Rows.map((Row, Row_Index) => (
                <article
                  key={`${Group.Tax_Year}-${Row.Installment}-${Row_Index}`}
                  className="rounded-xl border border-pq-border bg-slate-50 px-4 py-4"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-medium text-pq-fg">
                      Installment {Row.Installment}
                    </p>
                    {Row.Status && Row.Status !== "—" ? (
                      <span
                        className={`rounded-md border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${Tax_Status_Class(Row.Status)}`}
                      >
                        {Row.Status}
                      </span>
                    ) : null}
                  </div>
                  <dl className="mt-3 grid grid-cols-3 gap-2">
                    <div>
                      <dt className="font-mono text-[10px] uppercase tracking-wider text-pq-fg-subtle">
                        Ad valorem
                      </dt>
                      <dd className="mt-1 font-mono text-xs text-slate-200">{Row.Ad_Valorem}</dd>
                    </div>
                    <div>
                      <dt className="font-mono text-[10px] uppercase tracking-wider text-pq-fg-subtle">
                        Special
                      </dt>
                      <dd className="mt-1 font-mono text-xs text-slate-200">{Row.Special}</dd>
                    </div>
                    <div>
                      <dt className="font-mono text-[10px] uppercase tracking-wider text-pq-fg-subtle">
                        Total
                      </dt>
                      <dd className="mt-1 font-mono text-xs font-medium text-pq-fg">{Row.Total}</dd>
                    </div>
                  </dl>
                </article>
              ))}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function Sources_Table({ Rows }: { Rows: Source_Row[] }) {
  if (!Rows.length) return null;

  return (
    <div className={`overflow-hidden rounded-2xl ${ParcelQ_Card_Class}`}>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className={`border-b border-pq-border bg-slate-50 ${ParcelQ_Label_Mono_Class}`}>
              <th className="px-5 py-3 font-medium">Dataset</th>
              <th className="px-4 py-3 font-medium">Retrieved</th>
              <th className="px-4 py-3 font-medium">URL</th>
            </tr>
          </thead>
          <tbody>
            {Rows.map((Row) => (
              <tr key={Row.Source_Url} className="border-b border-slate-100 last:border-b-0 odd:bg-slate-50/80">
                <td className="px-5 py-3 text-slate-200">{Row.Dataset}</td>
                <td className="px-4 py-3 font-mono text-xs text-pq-fg-subtle">{Row.Retrieved}</td>
                <td className="px-4 py-3">
                  <a
                    href={Row.Source_Url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-all font-mono text-xs text-amber-800 underline-offset-2 hover:underline"
                  >
                    {Row.Source_Url}
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Badge_Verification({ Status }: { Status: Verification_Status }) {
  const Badge_Styles: Record<Verification_Status, string> = {
    Present: "border-amber-400/30 bg-amber-400/15 text-amber-200",
    Unverified: "border-amber-400/40 bg-amber-950/40 text-amber-300",
    "Not present": "border-pq-border bg-slate-100 text-pq-fg-subtle",
    "Present but criteria unverified":
      "border-amber-400/40 bg-amber-950/40 text-amber-300",
  };

  const Badge_Label: Record<Verification_Status, string> = {
    Present: "Present",
    Unverified: "Unverified",
    "Not present": "Not present",
    "Present but criteria unverified": "Criteria unverified",
  };

  return (
    <span
      className={`inline-block rounded-md border px-2 py-0.5 font-mono text-xs uppercase tracking-wider ${Badge_Styles[Status]}`}
      title={Status}
    >
      {Badge_Label[Status]}
    </span>
  );
}

type Amenity_Sort_Key = "Name" | "Category" | "Distance_Ft" | "Verification_Status";
type Amenity_Sort_Dir = "asc" | "desc";

const Amenity_Sort_Columns: Array<{ Key: Amenity_Sort_Key; Label: string }> = [
  { Key: "Name", Label: "Amenity" },
  { Key: "Category", Label: "Category" },
  { Key: "Distance_Ft", Label: "Distance" },
  { Key: "Verification_Status", Label: "Verification" },
];

function Amenities_Rows_Collapse_Unnamed(Rows: Amenity_Row[]): Amenity_Row[] {
  const Named: Amenity_Row[] = [];
  const Unnamed_By_Category = new Map<string, Amenity_Row[]>();

  for (const Row of Rows) {
    if (!Amenity_Name_Is_Unnamed(Row.Name)) {
      Named.push(Row);
      continue;
    }
    const Key = Row.Category.trim().toLowerCase() || "amenity";
    const Group = Unnamed_By_Category.get(Key) ?? [];
    Group.push(Row);
    Unnamed_By_Category.set(Key, Group);
  }

  const Summaries: Amenity_Row[] = [];
  for (const Group of Unnamed_By_Category.values()) {
    const Category = Group[0]?.Category?.trim() || "amenity";
    const Category_Label = Category.toLowerCase();
    const Count = Group.length;
    const Nearest_Ft = Math.min(...Group.map((Row) => Row.Distance_Ft));
    const Verification_Status: Verification_Status = Group.some(
      (Row) => Row.Verification_Status === "Present",
    )
      ? "Present"
      : Group.some((Row) => Row.Verification_Status === "Present but criteria unverified")
        ? "Present but criteria unverified"
        : "Unverified";

    Summaries.push({
      Id: `unnamed-summary::${Category_Label}`,
      Name: Count === 1 ? `Unnamed ${Category_Label}` : `Unnamed ${Category_Label} × ${Count}`,
      Category,
      Distance_Ft: Nearest_Ft,
      Barrier_Flag: "Unknown",
      Verification_Status,
      Is_Summary: true,
      Summary_Count: Count,
    });
  }

  return [...Named, ...Summaries];
}

function Amenities_Rows_Sort(
  Rows: Amenity_Row[],
  Key: Amenity_Sort_Key,
  Dir: Amenity_Sort_Dir,
): Amenity_Row[] {
  const Sign = Dir === "asc" ? 1 : -1;
  return [...Rows].sort((A, B) => {
    if (Key === "Distance_Ft") {
      return (A.Distance_Ft - B.Distance_Ft) * Sign;
    }
    const A_Text = String(A[Key] ?? "");
    const B_Text = String(B[Key] ?? "");
    return A_Text.localeCompare(B_Text, undefined, { sensitivity: "base", numeric: true }) * Sign;
  });
}

function Amenities_Rows_Group_By_Category(Rows: Amenity_Row[]): Array<{
  Category: string;
  Rows: Amenity_Row[];
  Nearest_Ft: number;
}> {
  const Map_Groups = new Map<string, Amenity_Row[]>();
  for (const Row of Rows) {
    const Key = Row.Category.trim() || "Other";
    const Group = Map_Groups.get(Key) ?? [];
    Group.push(Row);
    Map_Groups.set(Key, Group);
  }

  return [...Map_Groups.entries()]
    .map(([Category, Group_Rows]) => ({
      Category,
      Rows: Group_Rows,
      Nearest_Ft: Math.min(...Group_Rows.map((Row) => Row.Distance_Ft)),
    }))
    .sort((A, B) => A.Nearest_Ft - B.Nearest_Ft || A.Category.localeCompare(B.Category));
}

function Distance_Label(Row: Amenity_Row): string {
  if (Row.Is_Summary && (Row.Summary_Count ?? 0) > 1) {
    return `from ${Row.Distance_Ft.toLocaleString()} ft`;
  }
  return `${Row.Distance_Ft.toLocaleString()} ft`;
}

function Amenities_Table({ Rows }: { Rows: Amenity_Row[] }) {
  const [Sort_Key, Sort_Key_Set] = useState<Amenity_Sort_Key>("Category");
  const [Sort_Dir, Sort_Dir_Set] = useState<Amenity_Sort_Dir>("asc");
  const [Group_By_Category, Group_By_Category_Set] = useState(true);

  const Rows_Prepared = Amenities_Rows_Collapse_Unnamed(Rows);
  const Rows_Sorted = Amenities_Rows_Sort(Rows_Prepared, Sort_Key, Sort_Dir);
  const Groups = Group_By_Category
    ? Amenities_Rows_Group_By_Category(
        Amenities_Rows_Sort(Rows_Prepared, "Distance_Ft", "asc"),
      )
    : [{ Category: "", Rows: Rows_Sorted, Nearest_Ft: 0 }];

  const Nearest_Overall =
    Rows_Prepared.length > 0 ? Math.min(...Rows_Prepared.map((Row) => Row.Distance_Ft)) : null;

  function Sort_Handle_Click(Key: Amenity_Sort_Key) {
    Group_By_Category_Set(false);
    if (Sort_Key === Key && !Group_By_Category) {
      Sort_Dir_Set((Prev) => (Prev === "asc" ? "desc" : "asc"));
      return;
    }
    Sort_Key_Set(Key);
    Sort_Dir_Set("asc");
  }

  return (
    <div className={`overflow-hidden rounded-2xl ${ParcelQ_Card_Class}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-pq-border bg-gradient-to-r from-amber-50 to-white px-5 py-4">
        <div>
          <p className="text-sm font-semibold text-pq-fg">Nearby amenities</p>
          <p className={`mt-1 ${ParcelQ_Label_Mono_Class}`}>
            {Rows_Prepared.length} listed
            {Nearest_Overall != null ? ` · nearest ${Nearest_Overall.toLocaleString()} ft` : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5" role="toolbar" aria-label="Sort amenities">
          <button
            type="button"
            onClick={() => {
              Group_By_Category_Set(true);
              Sort_Key_Set("Category");
              Sort_Dir_Set("asc");
            }}
            className={`rounded-lg border px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-wider transition ${
              Group_By_Category
                ? "border-amber-400 bg-amber-100 text-amber-950"
                : "border-pq-border text-pq-fg-subtle hover:border-pq-border-strong hover:text-pq-fg-muted"
            }`}
          >
            By category
          </button>
          {Amenity_Sort_Columns.filter((Column) => Column.Key !== "Category").map((Column) => {
            const Active = !Group_By_Category && Sort_Key === Column.Key;
            return (
              <button
                key={Column.Key}
                type="button"
                onClick={() => Sort_Handle_Click(Column.Key)}
                className={`rounded-lg border px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-wider transition ${
                  Active
                    ? "border-amber-400 bg-amber-100 text-amber-950"
                    : "border-pq-border text-pq-fg-subtle hover:border-pq-border-strong hover:text-pq-fg-muted"
                }`}
                aria-sort={Active ? (Sort_Dir === "asc" ? "ascending" : "descending") : "none"}
              >
                {Column.Label}
                <span className="ml-1 opacity-70" aria-hidden>
                  {Active ? (Sort_Dir === "asc" ? "↑" : "↓") : ""}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="divide-y divide-slate-200">
        {Groups.map((Group) => (
          <div key={Group.Category || "all"} className="px-4 py-4 sm:px-5">
            {Group.Category ? (
              <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
                <h4 className="text-sm font-semibold text-pq-fg">{Group.Category}</h4>
                <p className={ParcelQ_Label_Mono_Class}>
                  {Group.Rows.length} · from {Group.Nearest_Ft.toLocaleString()} ft
                </p>
              </div>
            ) : null}

            <ul className="grid gap-2">
              {Group.Rows.map((Row, Index) => (
                <li
                  key={`amenity-${Index}-${Row.Id}`}
                  className="grid gap-3 rounded-xl border border-pq-border bg-slate-50 px-4 py-3.5 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate text-sm font-medium text-pq-fg">{Row.Name}</p>
                      {!Group.Category ? (
                        <span className="rounded-md border border-pq-border bg-slate-100 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-pq-fg-subtle">
                          {Row.Category}
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1.5 font-mono text-xs text-amber-800">{Distance_Label(Row)}</p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                    {Row.Is_Summary ? (
                      <span className="font-mono text-xs text-pq-fg-subtle">Summary</span>
                    ) : (
                      <Badge_Verification Status={Row.Verification_Status} />
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

type Dashboard_State_Props = {
  Tier_Title: string;
  Apn: string;
  Job: ReportJob | null;
  Parcel_Rows: Table_Row[];
  Assessment_Rows: Table_Row[];
  Tax_History_Rows: Tax_History_Row[];
  Zoning_Rows: Table_Row[];
  Setback_Rows: Table_Row[];
  Hazard_Rows: Table_Row[];
  Incentive_Rows?: Table_Row[];
  Utility_Rows?: Table_Row[];
  Agency_Rows?: Table_Row[];
  Public_Docs_Rows?: Table_Row[];
  Docs_Rows: Table_Row[];
  Sources_Rows: Source_Row[];
  Amenities_Rows: Amenity_Row[];
  Red_Flags: Red_Flag_Row[];
  Parcel_Latitude?: number;
  Parcel_Longitude?: number;
  Parcel_Rings?: number[][][];
  Radius_Miles?: number;
  Time_Saved_Hours?: number;
  Time_Saved_Pipeline_Minutes?: number;
  Dashboard_OnRestart: () => void;
};

const Report_Links = [
  { Key: "a", Label: "TTT Report", Hint: "PDF · Report A" },
  { Key: "b", Label: "Radius map report", Hint: "PDF · Report B" },
  { Key: "b_map", Label: "Radius map", Hint: "PNG" },
  { Key: "c", Label: "Amenities detail", Hint: "PDF · Report C" },
  { Key: "d", Label: "Amenities summary", Hint: "PDF · Report D" },
] as const;

const Severity_Styles: Record<string, string> = {
  high: "border-red-300 bg-gradient-to-r from-red-50 to-white",
  medium: "border-amber-300 bg-gradient-to-r from-amber-50 to-white",
  low: "border-pq-border bg-gradient-to-r from-slate-50 to-white",
};

const Severity_Badge: Record<string, string> = {
  high: "border-red-300 bg-red-100 text-red-800",
  medium: "border-amber-300 bg-amber-100 text-amber-900",
  low: "border-pq-border bg-slate-100 text-pq-fg-muted",
};

function Severity_Class(Severity: string): string {
  return Severity_Styles[Severity.toLowerCase()] ?? Severity_Styles.low;
}

function Severity_Badge_Class(Severity: string): string {
  return Severity_Badge[Severity.toLowerCase()] ?? Severity_Badge.low;
}

function High_Flag_Count(Flags: Red_Flag_Row[]): number {
  return Flags.filter((Flag) => Flag.Severity.toLowerCase() === "high").length;
}

export function Dashboard_State({
  Tier_Title,
  Apn,
  Job,
  Parcel_Rows,
  Assessment_Rows,
  Tax_History_Rows,
  Zoning_Rows,
  Setback_Rows,
  Hazard_Rows,
  Incentive_Rows = [],
  Utility_Rows = [],
  Agency_Rows = [],
  Public_Docs_Rows = [],
  Docs_Rows,
  Sources_Rows,
  Amenities_Rows,
  Red_Flags,
  Parcel_Latitude,
  Parcel_Longitude,
  Parcel_Rings,
  Radius_Miles,
  Time_Saved_Hours,
  Time_Saved_Pipeline_Minutes,
  Dashboard_OnRestart,
}: Dashboard_State_Props) {
  const [Amenity_View, Amenity_View_Set] = useState<Amenity_View_Mode>("all");
  const Assessment_Source_Url =
    Assessment_Rows.find((Row) => Row.Source_Url)?.Source_Url ||
    Assessment_Rows.find((Row) => /source portal/i.test(Row.Label))?.Source_Url;

  const Amenities_Filtered = useMemo(
    () => Amenities_Rows.filter((Row) => Amenity_Row_Matches_View(Row.Category, Amenity_View)),
    [Amenities_Rows, Amenity_View],
  );

  const Download_Items = useMemo(() => {
    if (!Job?.reports) return [];
    const Items: Array<{ Href: string; Label: string; Hint: string; External?: boolean }> = [];
    for (const Report of Report_Links) {
      if (!Job.reports?.[Report.Key]) continue;
      Items.push({
        Href: ParcelQ_Client.reports.Download_Url(Report.Key, Apn, Job.id),
        Label: Report.Label,
        Hint: Report.Hint,
      });
    }
    Items.push({
      Href: `${Api_Paths.V1.Amenities.Export}?apn=${encodeURIComponent(Apn)}`,
      Label: "Amenities CSV",
      Hint: "Spreadsheet export",
    });
    Items.push({
      Href: ParcelQ_Client.reports.Console_Log_Url(Job.id),
      Label: "Pipeline log",
      Hint: "Run console output",
      External: true,
    });
    return Items;
  }, [Apn, Job]);

  const Snapshot_Facts = useMemo(
    () => Snapshot_Facts_Build(Parcel_Rows, Assessment_Rows, Zoning_Rows),
    [Parcel_Rows, Assessment_Rows, Zoning_Rows],
  );

  const Has_Ttt =
    Parcel_Rows.length > 0 ||
    Assessment_Rows.length > 0 ||
    Tax_History_Rows.length > 0 ||
    Zoning_Rows.length > 0 ||
    Setback_Rows.length > 0 ||
    Hazard_Rows.length > 0 ||
    Incentive_Rows.length > 0 ||
    Utility_Rows.length > 0 ||
    Docs_Rows.length > 0;

  const Has_Amenities =
    Amenities_Rows.length > 0 || (Parcel_Latitude != null && Parcel_Longitude != null);

  const Nav_Items = useMemo(() => {
    const Items: Nav_Item[] = [{ Id: "section-flags", Label: "Flags" }];
    if (Download_Items.length) Items.push({ Id: "section-files", Label: "Files" });
    if (Has_Ttt) Items.push({ Id: "section-ttt", Label: "TTT" });
    if (Has_Amenities) Items.push({ Id: "section-amenities", Label: "Amenities" });
    if (Sources_Rows.length) Items.push({ Id: "section-sources", Label: "Sources" });
    return Items;
  }, [Download_Items.length, Has_Ttt, Has_Amenities, Sources_Rows.length]);

  const Flags_High = High_Flag_Count(Red_Flags);
  const City_County = Rows_Find_Value(Parcel_Rows, ["City / County"]);

  return (
    <section className="flex flex-1 flex-col px-5 py-8 sm:px-6 sm:py-12">
      <div className="mx-auto w-full max-w-6xl space-y-12 sm:space-y-16">
        <header className="relative overflow-hidden rounded-3xl border border-pq-border bg-pq-bg-elevated">
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 80% 70% at 10% -10%, rgba(34,211,238,0.16), transparent 55%), radial-gradient(ellipse 55% 50% at 100% 100%, rgba(14,116,144,0.14), transparent 50%), linear-gradient(180deg, rgba(16,28,46,0.4), transparent 40%)",
            }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.35]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(148,163,184,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.06) 1px, transparent 1px)",
              backgroundSize: "48px 48px",
              maskImage: "linear-gradient(180deg, black, transparent 85%)",
            }}
            aria-hidden
          />

          <div className="relative px-6 py-8 sm:px-10 sm:py-10">
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div className="min-w-0 max-w-2xl">
                <p className={ParcelQ_Label_Mono_Class}>Parcel Q report</p>
                <h2 className={`mt-3 font-display text-3xl sm:text-4xl ${ParcelQ_Heading_Class}`}>
                  {Tier_Title}
                </h2>
                <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-pq-fg-muted">
                  {Apn ? (
                    <span className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-pq-bg-soft px-3 py-1.5 font-mono text-xs text-amber-950">
                      <span className="text-amber-700">APN</span>
                      {Apn}
                    </span>
                  ) : null}
                  {City_County ? <span className="text-pq-fg-subtle">{City_County}</span> : null}
                </div>
              </div>
              <button type="button" onClick={Dashboard_OnRestart} className={ParcelQ_Btn_Secondary_Class}>
                New search
              </button>
            </div>

            <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-2xl border border-pq-border bg-pq-bg-soft/80 px-4 py-4 backdrop-blur-sm">
                <p className={ParcelQ_Label_Mono_Class}>Red flags</p>
                <p className="mt-2 font-display text-2xl font-semibold text-pq-fg">
                  {Red_Flags.length}
                  {Flags_High > 0 ? (
                    <span className="ml-2 text-sm font-medium text-red-300">{Flags_High} high</span>
                  ) : null}
                </p>
              </div>
              <div className="rounded-2xl border border-pq-border bg-pq-bg-soft/80 px-4 py-4 backdrop-blur-sm">
                <p className={ParcelQ_Label_Mono_Class}>Amenities</p>
                <p className="mt-2 font-display text-2xl font-semibold text-pq-fg">
                  {Amenities_Rows.length}
                </p>
              </div>
              <div className="rounded-2xl border border-pq-border bg-pq-bg-soft/80 px-4 py-4 backdrop-blur-sm">
                <p className={ParcelQ_Label_Mono_Class}>Deliverables</p>
                <p className="mt-2 font-display text-2xl font-semibold text-pq-fg">
                  {Download_Items.length}
                </p>
              </div>
              <div className="rounded-2xl border border-pq-border bg-pq-bg-soft/80 px-4 py-4 backdrop-blur-sm">
                <p className={ParcelQ_Label_Mono_Class}>Time saved</p>
                <p className="mt-2 font-display text-2xl font-semibold text-pq-fg">
                  {Time_Saved_Hours != null ? `~${Time_Saved_Hours}h` : "—"}
                </p>
                {Time_Saved_Pipeline_Minutes != null ? (
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-pq-fg-subtle">
                    machine ~{Time_Saved_Pipeline_Minutes} min
                  </p>
                ) : null}
              </div>
            </div>

            {Snapshot_Facts.length ? (
              <div className="mt-6 grid gap-px overflow-hidden rounded-2xl border border-pq-border bg-slate-200 sm:grid-cols-2 lg:grid-cols-3">
                {Snapshot_Facts.map((Fact) => (
                  <div key={Fact.Label} className="bg-pq-bg-soft/90 px-4 py-3.5">
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-pq-fg-subtle">
                      {Fact.Label}
                    </p>
                    <p className="mt-1.5 line-clamp-2 text-sm font-medium leading-snug text-pq-fg">
                      {Fact.Value}
                    </p>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </header>

        <nav
          className="sticky top-0 z-20 -mx-1 overflow-x-auto rounded-2xl border border-amber-900/10 bg-pq-bg/95 px-2 py-2 shadow-md shadow-amber-950/5 backdrop-blur-md"
          aria-label="Report sections"
        >
          <ul className="flex min-w-max gap-1">
            {Nav_Items.map((Item) => (
              <li key={Item.Id}>
                <a
                  href={`#${Item.Id}`}
                  className="block rounded-xl px-3.5 py-2 text-xs font-semibold uppercase tracking-wider text-pq-fg-muted transition hover:bg-amber-100 hover:text-amber-950"
                >
                  {Item.Label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <Report_Section
          Id="section-flags"
          Index="01"
          Title="Automatic Red Flags"
          Meta="Rules-based · public data only · not underwriting"
        >
          {Red_Flags.length ? (
            <ul className="grid gap-3">
              {Red_Flags.map((Flag) => (
                <li
                  key={Flag.Code}
                  className={`rounded-2xl border px-5 py-4 ${Severity_Class(Flag.Severity)}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <p className="text-base font-semibold text-pq-fg">{Flag.Label}</p>
                    <span
                      className={`rounded-md border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${Severity_Badge_Class(Flag.Severity)}`}
                    >
                      {Flag.Severity}
                    </span>
                  </div>
                  {Flag.Detail ? (
                    <p className="mt-2 max-w-3xl text-sm leading-relaxed text-pq-fg-muted">{Flag.Detail}</p>
                  ) : null}
                  {(Flag.Reason_Resident || Flag.Reason_Developer) && (
                    <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
                      {Flag.Reason_Resident ? (
                        <div className="rounded-lg border border-pq-border bg-pq-bg-elevated/80 px-3 py-2">
                          <dt className="font-mono text-[10px] uppercase tracking-widest text-pq-fg-subtle">
                            Why it matters — resident
                          </dt>
                          <dd className="mt-1 text-pq-fg-muted">{Flag.Reason_Resident}</dd>
                        </div>
                      ) : null}
                      {Flag.Reason_Developer ? (
                        <div className="rounded-lg border border-pq-border bg-pq-bg-elevated/80 px-3 py-2">
                          <dt className="font-mono text-[10px] uppercase tracking-widest text-pq-fg-subtle">
                            Why it matters — developer
                          </dt>
                          <dd className="mt-1 text-pq-fg-muted">{Flag.Reason_Developer}</dd>
                        </div>
                      ) : null}
                    </dl>
                  )}
                  {Flag.Source || Flag.Source_Url ? (
                    <p className="mt-3 text-sm">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-pq-fg-subtle">
                        Source{" "}
                      </span>
                      {Flag.Source_Url ? (
                        <a
                          href={Flag.Source_Url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-semibold text-amber-800 underline decoration-amber-600/70 underline-offset-2 hover:text-amber-950"
                        >
                          {Flag.Source || "Open source"}
                        </a>
                      ) : (
                        <span className="text-pq-fg-muted">{Flag.Source}</span>
                      )}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white px-6 py-8">
              <p className="text-sm font-medium text-emerald-900">No automatic red flags from available fields.</p>
              <p className={`mt-2 ${ParcelQ_Label_Mono_Class}`}>
                Flags fire only when gathered public data trips a rule
              </p>
            </div>
          )}
        </Report_Section>

        {Download_Items.length ? (
          <Report_Section
            Id="section-files"
            Index="02"
            Title="Deliverables"
            Meta="PDF · map · CSV · pipeline log"
          >
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {Download_Items.map((Item) => (
                <li key={Item.Href}>
                  <a
                    href={Item.Href}
                    {...(Item.External ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    className="group flex h-full flex-col justify-between rounded-2xl border border-pq-border bg-pq-bg-elevated p-5 transition hover:-translate-y-0.5 hover:border-amber-400 hover:bg-pq-bg-soft hover:shadow-lg hover:shadow-amber-950/10"
                  >
                    <div>
                      <p className="text-sm font-semibold text-pq-fg group-hover:text-pq-fg">
                        {Item.Label}
                      </p>
                      <p className={`mt-2 ${ParcelQ_Label_Mono_Class}`}>{Item.Hint}</p>
                    </div>
                    <span className="mt-5 font-mono text-xs text-amber-700 transition group-hover:text-amber-800">
                      Open →
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </Report_Section>
        ) : null}

        {Has_Ttt ? (
          <Report_Section
            Id="section-ttt"
            Index="03"
            Title="TTT Report"
            Meta="Parcel · tax / assessor · zoning · hazards · docs"
          >
            <div className="space-y-10">
              <div>
                <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.2em] text-pq-fg-subtle">
                  Identity & valuation
                </p>
                <div className="grid gap-4 lg:grid-cols-2 lg:items-stretch">
                  <Data_Table Title="Parcel & legal" Rows={Parcel_Rows} Accent />
                  <Data_Table Title="Assessment & tax" Rows={Assessment_Rows} Accent />
                </div>
              </div>

              {Tax_History_Rows.length ? (
                <div>
                  <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.2em] text-pq-fg-subtle">
                    Tax bill timeline
                  </p>
                  <Tax_History_Table Rows={Tax_History_Rows} Source_Url={Assessment_Source_Url} />
                </div>
              ) : null}

              {(Zoning_Rows.length > 0 || Setback_Rows.length > 0 || Hazard_Rows.length > 0) && (
                <div>
                  <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.2em] text-pq-fg-subtle">
                    Land use & risk
                  </p>
                  <div className="grid gap-4 lg:grid-cols-3 lg:items-stretch">
                    <Data_Table Title="Zoning & land use" Rows={Zoning_Rows} />
                    <Data_Table Title="Setbacks" Rows={Setback_Rows} />
                    <Data_Table Title="Environmental & hazards" Rows={Hazard_Rows} />
                  </div>
                </div>
              )}

              {(Incentive_Rows.length > 0 || Utility_Rows.length > 0 || Agency_Rows.length > 0) && (
                <div>
                  <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.2em] text-pq-fg-subtle">
                    Incentives, utilities & agencies
                  </p>
                  <div className="grid gap-4 lg:grid-cols-3 lg:items-stretch">
                    <Data_Table Title="Development incentives" Rows={Incentive_Rows} />
                    <Data_Table Title="Utilities & districts" Rows={Utility_Rows} />
                    <Data_Table Title="Agency contacts" Rows={Agency_Rows} />
                  </div>
                </div>
              )}

              {Docs_Rows.length ? (
                <div>
                  <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.2em] text-pq-fg-subtle">
                    Recorder index
                  </p>
                  <Data_Table Title="Recorded documents" Rows={Docs_Rows} />
                </div>
              ) : null}

              {Public_Docs_Rows.length ? (
                <div>
                  <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.2em] text-pq-fg-subtle">
                    Public documents & portals
                  </p>
                  <Data_Table Title="Public documents" Rows={Public_Docs_Rows} />
                </div>
              ) : null}
            </div>
          </Report_Section>
        ) : null}

        {Has_Amenities ? (
          <Report_Section
            Id="section-amenities"
            Index={Has_Ttt ? "04" : "03"}
            Title="Amenity Report"
            Meta="Nearby amenities · layer map · views"
            Actions={
              <div className="flex flex-wrap gap-2">
                {(
                  [
                    ["all", "All"],
                    ["livability", "Livability"],
                    ["affordable_housing", "Affordable housing"],
                  ] as const
                ).map(([Mode, Label]) => (
                  <button
                    key={Mode}
                    type="button"
                    onClick={() => Amenity_View_Set(Mode)}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                      Amenity_View === Mode
                        ? "border-amber-400 bg-amber-100 text-amber-950"
                        : "border-pq-border text-pq-fg-subtle hover:border-pq-border-strong hover:text-slate-200"
                    }`}
                  >
                    {Label}
                  </button>
                ))}
              </div>
            }
          >
            <div className="space-y-5">
              {Parcel_Latitude != null && Parcel_Longitude != null ? (
                <Amenities_Map
                  Latitude={Parcel_Latitude}
                  Longitude={Parcel_Longitude}
                  Rings={Parcel_Rings}
                  Amenities_Rows={Amenities_Filtered}
                  Radius_Miles={Radius_Miles ?? Job?.radius_miles ?? 1.5}
                />
              ) : null}

              {Amenities_Filtered.length ? (
                <Amenities_Table Rows={Amenities_Filtered} />
              ) : (
                <div className={`rounded-2xl px-5 py-8 text-center ${ParcelQ_Card_Class}`}>
                  <p className="text-sm text-pq-fg-subtle">No amenities in this view.</p>
                </div>
              )}
            </div>
          </Report_Section>
        ) : null}

        {Sources_Rows.length ? (
          <Report_Section
            Id="section-sources"
            Index={Has_Ttt && Has_Amenities ? "05" : Has_Ttt || Has_Amenities ? "04" : "03"}
            Title="Data sources"
            Meta="Attribution · retrieval timestamps · URLs"
          >
            <Sources_Table Rows={Sources_Rows} />
          </Report_Section>
        ) : null}
      </div>
    </section>
  );
}
